import React from "react";

// Helper function to determine medal type based on minutes
// Thresholds: Bronze (6h/360min), Silver (12h/720min), Gold (18h/1080min), Platinum (24h+/1440min+)
export const getMedalType = (minutes) => {
  const minutesNum = parseInt(minutes) || 0;
  if (minutesNum >= 1440) {
    return "platinum";
  } else if (minutesNum >= 1080) {
    return "gold";
  } else if (minutesNum >= 720) {
    return "silver";
  } else if (minutesNum >= 360) {
    return "bronze";
  }
  return null;
};

// Helper function to get minutes needed for next medal level
export const getMinutesToNextLevel = (minutes) => {
  const minutesNum = parseInt(minutes) || 0;
  if (minutesNum >= 1440) {
    return null; // Already at max level
  } else if (minutesNum >= 1080) {
    return 1440 - minutesNum; // To reach Platinum
  } else if (minutesNum >= 720) {
    return 1080 - minutesNum; // To reach Gold
  } else if (minutesNum >= 360) {
    return 720 - minutesNum; // To reach Silver
  } else {
    return 360 - minutesNum; // To reach Bronze
  }
};

// Helper function to get next medal level name
export const getNextMedalLevel = (minutes) => {
  const minutesNum = parseInt(minutes) || 0;
  if (minutesNum >= 1440) {
    return null; // Already at max level
  } else if (minutesNum >= 1080) {
    return "Platinum";
  } else if (minutesNum >= 720) {
    return "Gold";
  } else if (minutesNum >= 360) {
    return "Silver";
  } else {
    return "Bronze";
  }
};

const Medal = ({ type, size = 40, showLabel = true }) => {
  const medalStyles = {
    width: size,
    height: size,
    display: "inline-block",
  };

  const getMedalName = () => {
    switch (type) {
      case "bronze":
        return "Bronze";
      case "silver":
        return "Silver";
      case "gold":
        return "Gold";
      case "platinum":
        return "Platinum";
      default:
        return "";
    }
  };

  const getMedalSVG = () => {
    switch (type) {
      case "bronze":
        return (
          <svg
            viewBox="0 0 100 100"
            style={medalStyles}
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="50" cy="50" r="45" fill="#CD7F32" stroke="#8B4513" strokeWidth="3" />
            <circle cx="50" cy="50" r="35" fill="#D4A574" />
            <circle cx="50" cy="50" r="25" fill="#CD7F32" />
            <text
              x="50"
              y="55"
              textAnchor="middle"
              fontSize="20"
              fill="#8B4513"
              fontWeight="bold"
            >
              B
            </text>
            <path
              d="M 30 20 L 50 10 L 70 20 L 65 15 L 50 8 L 35 15 Z"
              fill="#8B4513"
            />
          </svg>
        );
      case "silver":
        return (
          <svg
            viewBox="0 0 100 100"
            style={medalStyles}
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="50" cy="50" r="45" fill="#C0C0C0" stroke="#808080" strokeWidth="3" />
            <circle cx="50" cy="50" r="35" fill="#E8E8E8" />
            <circle cx="50" cy="50" r="25" fill="#C0C0C0" />
            <text
              x="50"
              y="55"
              textAnchor="middle"
              fontSize="20"
              fill="#606060"
              fontWeight="bold"
            >
              S
            </text>
            <path
              d="M 30 20 L 50 10 L 70 20 L 65 15 L 50 8 L 35 15 Z"
              fill="#808080"
            />
          </svg>
        );
      case "gold":
        return (
          <svg
            viewBox="0 0 100 100"
            style={medalStyles}
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="50" cy="50" r="45" fill="#FFD700" stroke="#FFA500" strokeWidth="3" />
            <circle cx="50" cy="50" r="35" fill="#FFED4E" />
            <circle cx="50" cy="50" r="25" fill="#FFD700" />
            <text
              x="50"
              y="55"
              textAnchor="middle"
              fontSize="20"
              fill="#FF8C00"
              fontWeight="bold"
            >
              G
            </text>
            <path
              d="M 30 20 L 50 10 L 70 20 L 65 15 L 50 8 L 35 15 Z"
              fill="#FFA500"
            />
          </svg>
        );
      case "platinum":
        return (
          <svg
            viewBox="0 0 100 100"
            style={medalStyles}
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="50" cy="50" r="45" fill="#E5E4E2" stroke="#BCC6CC" strokeWidth="3" />
            <circle cx="50" cy="50" r="35" fill="#F5F5F5" />
            <circle cx="50" cy="50" r="25" fill="#E5E4E2" />
            <text
              x="50"
              y="55"
              textAnchor="middle"
              fontSize="18"
              fill="#8B8989"
              fontWeight="bold"
            >
              P
            </text>
            <path
              d="M 30 20 L 50 10 L 70 20 L 65 15 L 50 8 L 35 15 Z"
              fill="#BCC6CC"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
      {getMedalSVG()}
      {showLabel && (
        <span style={{ fontSize: "14px", fontWeight: "500", color: "var(--brand)" }}>
          {getMedalName()}
        </span>
      )}
    </div>
  );
};

export default Medal;

